/**
 * ReportsOverviewAdmin — the ADMIN-only Reports Overview.
 *
 * A fill-rate / occupancy console (spec: uploads/Event-Performance-Report-Build-Spec.md),
 * BLENDED with the executive money+goal signals so an admin still sees revenue
 * and pacing at a glance (operator decision, 2026-07-09). Only the System
 * Administrator role renders this; every other reports.read role keeps the
 * standard executive scorecard (see ReportsOverview.tsx branch).
 *
 * Layout (top → bottom):
 *   Global stats bar (all events, never filtered)
 *   Past Performance / Future Outlook toggle  +  live range + location filters
 *   Findings band: thresholded fill findings, each with an owner and one verb
 *   KPI scorecard: Efficiency · Perfect Execution · Missed Opps · Attendees · Gross revenue (goal)
 *   Every session ranked (the former By-event tab, merged 2026-08-31)
 *   Grouped drill-down: month → event → occurrence table, with roster CSV downloads
 *
 * All fill numbers come from reports-fill (derived 1:1 from the op-backed
 * events list — nothing fabricated); revenue + goal come from the SAME ops the
 * standard Overview uses (getReportsOverview + getReportGoals); rosters and the
 * registration exports fetch the event's REAL registrations
 * (listEventRegistrations) — awaited before any file is produced.
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { Btn, SegmentedControl, Card, Dropdown, Icon, Pill, T, tx, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { can, isAdmin, isSignedIn, onTenantSwitching, subscribeAccess } from "../../shell/access";
import ConfirmModal from "../../shell/ConfirmModal";
import { logAction } from "../../lib/audit";
import { getReportsOverview, reportsMoney, drilldowns } from "@v0y4ge/api-client";
// listEventRegistrations is deliberately NAMESPACED in the client barrel
// (creation + drilldowns collide on updateEventRegistration) — drop29 amend.
const { listEventRegistrations } = drilldowns;
import { REPORTS_MONEY_OPTS, REPORTS_OVERVIEW_OPTS } from "../../shell/request-opts";
import { useEvents } from "../events/useEvents";
import { reportTabs, fmtNum, fmtMoney, toReportData, buildEmpty, delta } from "./ReportsOverview";
import {
  buildFillData, fetchFillOccurrences, globalStats, execMetrics, groupForDrilldown, storeOptions,
  fillTone, FILL_TONE_LABEL, type FillOccurrence, type FillTone, type MonthGroup,
} from "./reports-fill";
import { eventFindings, UNDER_RUN, STRONG, type EventFinding } from "./event-findings";
import { FindingCard, FindingsHeader } from "./findings-kit";
import { useT, t as i18nT } from "../../lib/i18n";

/* Analyze presets — window lengths, labelled Next… (future) / Last… (past) to
 * match the mode; "custom" reveals explicit From/To date pickers. */
const ANALYZE = [
  { id: "7d", days: 7, label: "7 days", labelKey: "reports.overviewAdmin.text7Days" },
  { id: "14d", days: 14, label: "14 days", labelKey: "reports.overviewAdmin.text14Days" },
  { id: "30d", days: 30, label: "30 days", labelKey: "reports.overviewAdmin.text30Days" },
  { id: "90d", days: 90, label: "90 days", labelKey: "reports.overviewAdmin.text90Days" },
  { id: "6m", days: 182, label: "6 months", labelKey: "reports.overviewAdmin.text6Months" },
  { id: "12m", days: 365, label: "12 months", labelKey: "reports.overviewAdmin.text12Months" },
];
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/* Comparison baselines — what the analysis window is measured against.
 * `offset` is how far back the baseline window sits: "window" = the immediately
 * preceding window of the same length; a number = that many days back (so the
 * same-length window one month / quarter / year earlier). */
const COMPARE = [
  { id: "prev", label: "vs. previous period", labelKey: "reports.overviewAdmin.vsPreviousPeriod", short: "vs prev period", offset: "window" as const },
  { id: "month", label: "vs. previous month", labelKey: "reports.overviewAdmin.vsPreviousMonth", short: "vs prev month", offset: 30 },
  { id: "quarter", label: "vs. previous quarter", labelKey: "reports.overviewAdmin.vsPreviousQuarter", short: "vs prev quarter", offset: 91 },
  { id: "year", label: "vs. same period last year", labelKey: "reports.overviewAdmin.vsSamePeriodLastYear", short: "vs last year", offset: 365 },
  { id: "none", label: "No comparison", labelKey: "reports.overviewAdmin.noComparison", short: "", offset: null as null | number | "window" },
];

/* Delta chips for the exec cards, given the comparison window's metrics. */
function ptsChip(cur: number, prev: number, short: string) { const d = cur - prev; return { text: `${d >= 0 ? "\u2191" : "\u2193"} ${Math.abs(d)} pts ${short}`, up: d >= 0 }; }
function countChip(cur: number, prev: number, short: string, lowerBetter: boolean) { const d = cur - prev; return { text: `${d >= 0 ? "+" : "\u2212"}${Math.abs(d)} ${short}`, up: lowerBetter ? d <= 0 : d >= 0 }; }
function pctChip(cur: number, prev: number, short: string) { const pct = prev > 0 ? ((cur - prev) / prev) * 100 : 0; return { text: `${pct >= 0 ? "\u2191" : "\u2193"} ${Math.abs(pct).toFixed(0)}% ${short}`, up: pct >= 0 }; }

const TONE_FG: Record<FillTone, string> = { strong: T.green1, moderate: T.orange1, attention: T.red1 };

/* ── Fill-rate badge — colour + text (never colour alone) ─────────────── */
function FillPill({ pct, size = 13 }: { pct: number; size?: number }) {
  const tone = fillTone(pct);
  const fg = TONE_FG[tone];
  return (
    <span
      title={FILL_TONE_LABEL[tone]}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 999, background: alpha(fg, 14), color: fg, fontWeight: 700, fontSize: size, fontVariantNumeric: "tabular-nums", lineHeight: 1.6 }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: fg }} />
      {pct}%
    </span>
  );
}

/* ── Executive metric card — coloured top border (spec §3.4) ──────────── */
function ExecCard({ label, value, accent, sub, delta }: { label: string; value: string; accent: string; sub?: string; delta?: { text: string; up: boolean } }) {
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ height: 4, background: accent }} />
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
        {delta ? <div style={{ fontSize: 12, color: delta.up ? T.green1 : T.red1, marginTop: 4, fontWeight: 500 }}>{delta.text}</div> : null}
        <div style={{ fontSize: 12, fontWeight: 600, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04, marginTop: 4 }}>{label}</div>
        {sub ? <div style={{ fontSize: 12, color: T.soft, marginTop: 6 }}>{sub}</div> : null}
      </div>
    </Card>
  );
}

/* ── Revenue card with goal bar (the money+goal blend) ────────────────── */
function RevenueCard({ value, accent, deltaText, up, goalPct }: { value: string; accent: string; deltaText: string; up: boolean; goalPct: number | null }) {
  const t = useT();
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ height: 4, background: accent }} />
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
        {deltaText ? <div style={{ fontSize: 12, color: up ? T.green1 : T.red1, marginTop: 4, fontWeight: 500 }}>{deltaText}</div> : null}
        <div style={{ fontSize: 12, fontWeight: 600, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04, marginTop: 4 }}>{t("reports.overviewAdmin.grossRevenue", "Gross revenue")}</div>
        <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{t("reports.overviewAdmin.workspaceLast30Days", "Workspace · last 30 days")}</div>
        {goalPct != null ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ height: 4, background: T.n5, borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, goalPct)}%`, background: accent, transition: "width 400ms" }} />
          </div>
          <div style={{ fontSize: 11, color: T.soft, marginTop: 3 }}>{goalPct}{t("reports.overviewAdmin.ofGoal", "% of goal")}</div>
        </div>
        ) : null}
      </div>
    </Card>
  );
}

/* ── Roster rows — the event's REAL registrations (listEventRegistrations,
 * GET /v1/admin/events/{eventId}/registrations). Fetched + AWAITED before any
 * CSV is produced; a per-render cache avoids refetching the same event across
 * the roster / group / period exports. No fabricated attendees. ─────────── */
const ROSTER_HEAD = ["First name", "Last name", "Email", "Phone", "Company", "Ticket", "Status", "Source", "Registered at"];
type RegRow = string[];
const _regCache = new Map<string, RegRow[]>();
/* Registrants' names, emails and phones: dropped with the session and on a workspace switch
   (CC, 2026-10-07), so the next person signing in cannot export the previous one's rows. */
let _regEpoch = 0;
const dropRegCache = () => { _regCache.clear(); _regEpoch++; };
if (typeof onTenantSwitching === "function") onTenantSwitching(dropRegCache);
if (typeof subscribeAccess === "function") subscribeAccess(() => { if (!isSignedIn()) dropRegCache(); });
export async function fetchRegistrationRows(eventId: string): Promise<RegRow[]> {
  const hit = _regCache.get(eventId);
  if (hit) return hit;
  const epoch = _regEpoch;
  const s = (v: unknown) => (v == null ? "" : String(v));
  const rows: RegRow[] = [];
  // The server clamps `limit` to 200 per page — walk hasMore/next_cursor until
  // exhausted so large events export a COMPLETE roster (drop29 paging verdict).
  let cursor: string | undefined;
  let page = 1;
  for (let i = 0; i < 100; i++) {
    const res = await listEventRegistrations(eventId, { limit: 200, ...(cursor ? { cursor } : { page }) } as never) as {
      registrations?: Record<string, unknown>[]; hasMore?: boolean; next_cursor?: string | null; nextCursor?: string | null;
    };
    const arr = res?.registrations ?? [];
    arr.forEach((r) => rows.push([
      s(r.firstName ?? r.first_name), s(r.lastName ?? r.last_name), s(r.email),
      s(r.phone), s(r.company), s(r.ticketName ?? r.ticket_name),
      s(r.status), s(r.source), s(r.registeredAt ?? r.registered_at),
    ]));
    const next = (res?.next_cursor ?? res?.nextCursor) || undefined;
    if (!res?.hasMore || (!next && arr.length === 0)) break;
    cursor = next;
    page += 1;
  }
  if (epoch === _regEpoch) _regCache.set(eventId, rows); // not after a sign-out or switch
  return rows;
}
async function rosterRows(occ: FillOccurrence): Promise<string[][]> {
  return [ROSTER_HEAD, ...(await fetchRegistrationRows(occ.eventId))];
}
function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(",")).join("\n");
}
function download(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.rel = "noopener"; a.target = "_blank"; a.style.display = "none";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/* Registration-level export — one row per registered customer, with every
 * customer detail we hold plus the event/occurrence context. `periodLabel`
 * adds a Period column so a combined current+comparison export stays legible. */
async function registrationCsv(occs: FillOccurrence[], periodLabel?: string): Promise<string[][]> {
  const head = [
    ...(periodLabel ? ["Period"] : []),
    "Event", "Location", "Date", "Time", "Event ID", "Fill %",
    ...ROSTER_HEAD,
  ];
  const rows: string[][] = [head];
  const sorted = occs.slice().sort((a, b) => a.monthSort.localeCompare(b.monthSort) || a.datetime - b.datetime);
  for (const o of sorted) {
    (await fetchRegistrationRows(o.eventId)).forEach((r) => rows.push([
      ...(periodLabel ? [periodLabel] : []),
      o.name, o.storeName, o.displayDate, o.time, o.eventId, `${o.fill}%`, ...r,
    ]));
  }
  return rows;
}

/* ═══ Main screen ════════════════════════════════════════════════════ */
export default function ReportsOverviewAdmin() {
  const t = useT();
  const analyzeOptions = ANALYZE.map((option) => ({ ...option, label: t(option.labelKey, option.label) }));
  const compareOptions = COMPARE.map((option) => ({ ...option, label: t(option.labelKey, option.label) }));
  const nav = useNav();
  const { events } = useEvents();
  // 2026-07-14 (rides the T0900 href-parsing fix) — dashboard KPI tiles / alert
  // CTAs land here carrying ?range&from&to (&metric, informational): preselect
  // the analyze window from them. Explicit from/to (slashed YYYY/MM/DD or ISO
  // YYYY-MM-DD) become a custom range; range=today ⇒ custom over today; a bare
  // preset id (7d/30d/…) maps straight onto ANALYZE. `metric` (e.g. events/run)
  // has no per-metric view on this console yet — accepted and ignored.
  const isoParam = (v: unknown): string | null => {
    const s = String(v ?? "").trim().replace(/\//g, "-");
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };
  const pFrom = isoParam(nav.params.from), pTo = isoParam(nav.params.to);
  const pRange = String(nav.params.range ?? "");
  const paramRange = (pFrom && pTo) || pRange === "today" ? "custom" : (ANALYZE.some((a) => a.id === pRange) ? pRange : null);
  const [range, setRange] = useState(paramRange ?? "12m");
  const [compare, setCompare] = useState("prev");
  const [locSel, setLocSel] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"past" | "future">("future");
  const [customStart, setCustomStart] = useState(() => pFrom ?? isoDay(Date.now()));
  const [customEnd, setCustomEnd] = useState(() => pTo ?? (paramRange === "custom" ? isoDay(Date.now()) : isoDay(Date.now() + 30 * 86400000)));
  // Re-apply when a NEW deep-link lands while this screen is already mounted
  // (e.g. clicking a second KPI tile) — the initializers above only run once.
  const paramSig = `${pRange}|${pFrom}|${pTo}`;
  useEffect(() => {
    if (!paramRange) return;
    setRange(paramRange);
    setCustomStart(pFrom ?? isoDay(Date.now()));
    if (paramRange === "custom") setCustomEnd(pTo ?? isoDay(Date.now()));
    else if (pTo) setCustomEnd(pTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramSig]);
  const [raw, setRaw] = useState<any>(null);
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());
  const [openEvents, setOpenEvents] = useState<Set<string>>(new Set());
  const [expOpen, setExpOpen] = useState(false);
  const exportOk = can("reports.export");

  // Revenue reads the SAME op + window the standard Overview uses (its own
  // 30-day money window — labelled on the card; the fill console's Analyze
  // window governs the fill metrics, not the money op).
  useEffect(() => { getReportsOverview({ range: "30d" } as never, REPORTS_OVERVIEW_OPTS).then(setRaw).catch(() => setRaw({})); }, []);
  // Real goal target for the revenue card — getReportGoals pacing[] (backend-scaled).
  const [revPace, setRevPace] = useState<{ actual?: number; target?: number } | null>(null);
  useEffect(() => {
    let live = true;
    reportsMoney.getReportGoals({ range: "30d" } as never, REPORTS_MONEY_OPTS)
      .then((res: any) => { if (live) setRevPace(res?.pacing?.find((x: any) => x.metric === "revenue") ?? null); })
      .catch(() => { if (live) setRevPace(null); });
    return () => { live = false; };
  }, []);

  const [now] = useState(() => Date.now());
  // REQ-026 (wired): prefer the real getOccupancyReport (true multi-occurrence
  // chains); buildFillData(events) is the initial paint + the fallback if the
  // op is absent or errors. No UI change either way — same FillOccurrence[] shape.
  //
  // #805 — getOccupancyReport is events-INDEPENDENT, so it fetches ONCE on
  // mount with a stable dep, reading the events fallback input via a ref.
  // Previously the effect keyed on the useEvents() ARRAY IDENTITY: when the
  // events store hydrated ([] → loaded) the dep change tore down the in-flight
  // effect (live=false) and the resolved occupancy rows were discarded by the
  // guard before reaching setAll — the admin fill console rendered 0 everything.
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const [all, setAll] = useState<FillOccurrence[]>(() => buildFillData(events, now));
  useEffect(() => {
    let live = true;
    fetchFillOccurrences(eventsRef.current, now).then((rows) => { if (live && rows.length) setAll(rows); });
    return () => { live = false; };
  }, [now]);
  // Events-based fallback in its OWN effect: only fills in when the op path
  // produced nothing (op absent/errored, or it fell back while events were
  // still pre-hydration empty). Never clobbers resolved occupancy rows.
  useEffect(() => {
    setAll((prev) => (prev.length ? prev : buildFillData(events, now)));
  }, [events, now]);
  const locOptions = useMemo(() => storeOptions(all), [all]);
  const gStats = useMemo(() => globalStats(all), [all]);

  // Sections below react to toggle + analyze window + location; the global bar does not.
  const isCustom = range === "custom";
  const analyzeMeta = analyzeOptions.find((a) => a.id === range);
  const windowMs = (analyzeMeta?.days ?? 30) * 86400000;
  let curStart: number, curEnd: number;
  if (isCustom && customStart && customEnd) {
    curStart = new Date(`${customStart}T00:00:00`).getTime();
    curEnd = new Date(`${customEnd}T23:59:59`).getTime();
    if (curEnd < curStart) { const t = curStart; curStart = curEnd; curEnd = t; }
  } else {
    curStart = mode === "past" ? now - windowMs : now;
    curEnd = mode === "past" ? now : now + windowMs;
  }
  const windowLenMs = Math.max(1, curEnd - curStart);
  const locKey = [...locSel].sort().join("|");
  const inLoc = (o: FillOccurrence) => locSel.size === 0 || locSel.has(o.storeName);
  const filtered = useMemo(
    () => all.filter((o) => inLoc(o) && o.datetime >= curStart && o.datetime <= curEnd),
    [all, locKey, curStart, curEnd],
  );

  // Comparison window = the analysis window shifted back by the chosen baseline,
  // so the SAME length of window (and its events) is measured period-over-period.
  const cmpMeta = compareOptions.find((c) => c.id === compare) ?? compareOptions[0];
  const cmpOffset = cmpMeta.offset == null ? null : (cmpMeta.offset === "window" ? windowLenMs : cmpMeta.offset * 86400000);
  const cmpRows = useMemo(
    () => cmpOffset == null ? null : all.filter((o) => inLoc(o) && o.datetime >= curStart - cmpOffset && o.datetime <= curEnd - cmpOffset),
    [all, locKey, curStart, curEnd, cmpOffset],
  );

  const cmpShort = cmpMeta.short;

  /* ── Findings (2026-08-31) ───────────────────────────────────────────
   * "Attention needed" was ten rows sorted by fill with no threshold, no owner
   * and no action. These are the same occurrences read as findings, in the
   * grammar Campus Pulse uses. Built on the UNSCOPED window, so selecting a
   * card cannot redefine its own membership. */
  const [finding, setFinding] = useState<string | null>(null);
  const [bandOpen, setBandOpen] = useState(true);
  const [verbStates, setVerbStates] = useState<Record<string, { state: "done"; n: number; at: number }>>({});
  const [confirmVerb, setConfirmVerb] = useState<EventFinding | null>(null);
  const windowLbl = isCustom ? "the custom range" : `${mode === "future" ? "next" : "last"} ${analyzeMeta?.label ?? "30 days"}`;
  const findings = useMemo(() => eventFindings(filtered, mode, windowLbl), [filtered, mode, windowLbl]);
  /* "Filter report" scopes the report below the findings. Bring that scoped report into view and move
   * focus to its "Scoped to …" line, so the change is seen and announced. Clearing does not scroll back. */
  const scopeRef = useRef<HTMLDivElement | null>(null);
  const revealScope = useRef(false);
  useEffect(() => {
    if (!revealScope.current || !finding) return;
    revealScope.current = false;
    const el = scopeRef.current;
    if (!el) return;
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    el.focus({ preventScroll: true });
  }, [finding]);
  const scope = useMemo(() => {
    const f = finding ? findings.find((x) => x.id === finding) : null;
    return f && f.occurrences.length ? { ids: new Set(f.occurrences.map((o) => o.id)), label: t("reports.overviewAdmin.findingScope", "{value} {headline}", { value: f.value, headline: f.headline }), n: f.occurrences.length } : null;
  }, [finding, findings]);
  /* A card filter re-scopes the WHOLE console, exactly as it does in Campus
     Pulse — the KPI band included, so no figure on the page belongs to a
     different population than the table under it. */
  const shown = useMemo(() => (scope ? filtered.filter((o) => scope.ids.has(o.id)) : filtered), [filtered, scope]);
  const canActEvents = isAdmin() || can("events.write");
  const runVerb = (f: EventFinding) => {
    if (!f.verb) return;
    logAction("event", `reports-events:${f.id}`, `Events Overview · ${f.headline}`, f.verb.id, {
      changes: [
        { field: "owner", from: null, to: f.owner },
        { field: "sessions", from: null, to: f.occurrences.length },
        { field: "window", from: null, to: windowLbl },
      ],
    });
    setVerbStates((x) => ({ ...x, [f.id]: { state: "done", n: f.occurrences.length, at: Date.now() } }));
    toast({ tone: "success", title: f.verb.done, subtitle: t("reports.overviewAdmin.sessionsRouted", "{count, plural, one {# session} other {# sessions}} · {owner}", { count: f.occurrences.length, owner: f.owner }) });
  };

  const rankable = useMemo(() => shown.filter((o) => o.spaces > 0), [shown]);
  const ranked = useMemo(() => [...rankable].sort((a, b) => b.fill - a.fill), [rankable]);
  /* The benchmark the By-event report ranked against: the window’s own fill,
     computed on seats rather than as an average of percentages, so one tiny
     session cannot swing it. */
  const execWindowAvg = useMemo(() => {
    const sp = rankable.reduce((s2, o) => s2 + o.spaces, 0);
    return sp ? Math.round((rankable.reduce((s2, o) => s2 + o.booked, 0) / sp) * 100) : 0;
  }, [rankable]);
  const months = useMemo(() => groupForDrilldown(shown), [shown]);

  /* The KPI band reads the SCOPED set, so no figure on this page belongs to a
     different population than the table under it. */
  const exec = useMemo(() => execMetrics(shown), [shown]);
  const execCmp = useMemo(() => cmpRows ? execMetrics(cmpRows) : null, [cmpRows]);
  const dEff = execCmp && !scope ? ptsChip(exec.efficiency, execCmp.efficiency, cmpShort) : undefined;
  const dPerfect = execCmp && !scope ? countChip(exec.perfect, execCmp.perfect, cmpShort, false) : undefined;
  const dMissed = execCmp && !scope ? countChip(exec.missed, execCmp.missed, cmpShort, true) : undefined;
  const dAtt = execCmp && !scope ? pctChip(exec.attendees, execCmp.attendees, cmpShort) : undefined;

  // Open every month by default whenever the grouping changes.
  useEffect(() => { setOpenMonths(new Set(months.map((m) => m.monthSort))); }, [mode, range, locKey, customStart, customEnd]);

  // Revenue + goal blend (same ops as the standard Overview). The delta only
  // renders when the response carries a REAL prior figure (never a scaled
  // ratio); the goal bar only renders when getReportGoals returns a target.
  const b = raw ? toReportData(raw, range) : buildEmpty();
  const dRev = b.prev.gross != null ? delta(b.kpis.gross, b.prev.gross) : null;
  const revGoalPct = revPace?.target && revPace.target > 0 ? Math.round((b.kpis.gross / revPace.target) * 100) : null;
  const revDeltaText = dRev ? `${dRev.up ? "\u2191" : "\u2193"} ${Math.abs(dRev.pct).toFixed(0)}% vs prior period` : "";

  const rangeLabel = isCustom
    ? (customStart && customEnd ? `${customStart} → ${customEnd}` : "custom range")
    : `${mode === "future" ? "next" : "last"} ${analyzeMeta?.label ?? "30 days"}`;
  const modeLabel = mode === "past" ? "past performance" : "future outlook";

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    setter((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const periodTag = isCustom ? `${customStart}_${customEnd}` : `${mode}-${range}`;
  const curRegs = filtered.reduce((s, o) => s + o.booked, 0);
  const cmpRegs = cmpRows ? cmpRows.reduce((s, o) => s + o.booked, 0) : 0;
  // Every export AWAITS the real registration fetch before a file is produced;
  // a fetch failure surfaces a danger toast and produces NOTHING (no fake file).
  const [exporting, setExporting] = useState(false);
  const exportFail = (e: unknown) => {
    console.warn("registration export failed:", e);
    toast({ tone: "danger", title: t("reports.overviewAdmin.couldnTFetchRegistrations", "Couldn't fetch registrations"), subtitle: t("reports.overviewAdmin.nothingWasExportedRetryInAMoment", "Nothing was exported. Retry in a moment.") });
  };
  const exportCurrent = async () => {
    if (!exportOk || exporting) return;
    setExporting(true);
    const epoch = _regEpoch; // a file finished after a sign-out or switch is not downloaded
    try {
      const rows = await registrationCsv(filtered);
      if (epoch !== _regEpoch) return;
      download(`event-registrations-${periodTag}.csv`, toCsv(rows));
      toast({ tone: "success", title: t("reports.overviewAdmin.registrationsExported", "Registrations exported"), subtitle: i18nT("reports.overviewAdmin.rowsLength1RegistrationsCurrentPeriod.icu", "{value0} registrations · current period", { value0: rows.length - 1 }) });
    } catch (e) { exportFail(e); } finally { setExporting(false); }
  };
  const exportComparison = async () => {
    if (!exportOk || !cmpRows || exporting) return;
    setExporting(true);
    const epoch = _regEpoch; // a file finished after a sign-out or switch is not downloaded
    try {
      const rows = await registrationCsv(cmpRows);
      if (epoch !== _regEpoch) return;
      download(`event-registrations-${periodTag}-comparison.csv`, toCsv(rows));
      toast({ tone: "success", title: t("reports.overviewAdmin.registrationsExported", "Registrations exported"), subtitle: i18nT("reports.overviewAdmin.rowsLength1RegistrationsCmpmetaShort.icu", "{value0} registrations · {value1}", { value0: rows.length - 1, value1: cmpMeta.short }) });
    } catch (e) { exportFail(e); } finally { setExporting(false); }
  };
  const exportBoth = async () => {
    if (!exportOk || !cmpRows || exporting) return;
    setExporting(true);
    const epoch = _regEpoch; // a file finished after a sign-out or switch is not downloaded
    try {
      const cur = await registrationCsv(filtered, "Current period");
      const cmp = await registrationCsv(cmpRows, `Comparison (${cmpMeta.short})`);
      const rows = [cur[0], ...cur.slice(1), ...cmp.slice(1)];
      if (epoch !== _regEpoch) return;
      download(`event-registrations-${periodTag}-current+comparison.csv`, toCsv(rows));
      toast({ tone: "success", title: t("reports.overviewAdmin.registrationsExported", "Registrations exported"), subtitle: i18nT("reports.overviewAdmin.rowsLength1RegistrationsBothPeriods.icu", "{value0} registrations · both periods", { value0: rows.length - 1 }) });
    } catch (e) { exportFail(e); } finally { setExporting(false); }
  };
  const downloadRoster = async (occ: FillOccurrence) => {
    if (!exportOk || exporting) return;
    setExporting(true);
    const epoch = _regEpoch; // a file finished after a sign-out or switch is not downloaded
    try {
      const rows = await rosterRows(occ);
      if (epoch !== _regEpoch) return;
      download(`roster-${occ.name.replace(/\W+/g, "-").toLowerCase()}-${occ.displayDate.replace(/\s/g, "")}.csv`, toCsv(rows));
      toast({ tone: "success", title: t("reports.overviewAdmin.rosterDownloaded", "Roster downloaded"), subtitle: rows.length > 1 ? `${rows.length - 1} registrations · ${occ.storeName}` : `No registrations yet · ${occ.storeName}` });
    } catch (e) { exportFail(e); } finally { setExporting(false); }
  };
  const downloadGroupRosters = async (name: string, occs: FillOccurrence[]) => {
    if (!exportOk || exporting) return;
    setExporting(true);
    const epoch = _regEpoch; // a file finished after a sign-out or switch is not downloaded
    try {
      const rows: string[][] = [["Occurrence", "Location", "Date", ...ROSTER_HEAD]];
      for (const o of occs) {
        (await fetchRegistrationRows(o.eventId)).forEach((r) => rows.push([o.name, o.storeName, o.displayDate, ...r]));
      }
      if (epoch !== _regEpoch) return;
      download(`rosters-${name.replace(/\W+/g, "-").toLowerCase()}-all.csv`, toCsv(rows));
      toast({ tone: "success", title: t("reports.overviewAdmin.allRostersDownloaded", "All rosters downloaded"), subtitle: i18nT("reports.overviewAdmin.nameRowsLength1Registrations.icu", "{value0} · {value1} registrations", { value0: name, value1: rows.length - 1 }) });
    } catch (e) { exportFail(e); } finally { setExporting(false); }
  };

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "overview")} activeTab="overview">
      <PageHeader
        title={t("reports.overviewAdmin.reports", "Reports")}
        subtitle={<span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{`${t("reports.overviewAdmin.eventPerformance", "Event performance")} · ${isCustom ? rangeLabel : `${modeLabel} · ${rangeLabel}`}${cmpMeta.offset != null ? ` · ${cmpMeta.short}` : ""}${locSel.size ? ` · ${locSel.size === 1 ? [...locSel][0] : t("reports.overviewAdmin.locationsCount", "{count, plural, one {# location} other {# locations}}", { count: locSel.size })}` : ""}`}</span>}
        breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("dashboard")}>{t("reports.overviewAdmin.dashboard", "← Dashboard")}</span>}
        actions={<>
          <LocationMultiSelect options={locOptions} selected={locSel} onChange={setLocSel} />
          <Dropdown<string> portal aria-label={t("reports.overviewAdmin.timeframeToAnalyze", "Timeframe to analyze")} value={range} onChange={setRange} style={{ width: 230 }}
            options={[...analyzeOptions.map((a) => ({ value: a.id, label: (mode === "future" ? t("reports.overviewAdmin.analyzeNext", "Analyze: Next {period}", { period: a.label }) : t("reports.overviewAdmin.analyzeLast", "Analyze: Last {period}", { period: a.label })) })),
              { value: "custom", label: t("reports.overviewAdmin.analyzeCustomRange", "Analyze: Custom range…") }]} />
          {isCustom ? <>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} style={dateStyle} aria-label={t("reports.overviewAdmin.fromDate", "From date")} />
            <span style={{ color: T.soft, fontSize: 13 }}>{t("reports.overviewAdmin.to", "to")}</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} style={dateStyle} aria-label={t("reports.overviewAdmin.toDate", "To date")} />
          </> : null}
          <Dropdown<string> portal aria-label={t("reports.overviewAdmin.compareTo", "Compare to")} value={compare} onChange={setCompare} style={{ width: 250 }}
            options={compareOptions.map((c) => ({ value: c.id, label: t("reports.overviewAdmin.text15", "Compare: {arg1}", { arg1: c.label.replace(/^vs\. /, "") }) }))} />
          <div style={{ position: "relative" }}>
            <Btn variant="secondary" icon={<Icon.download size={14} />} disabled={!exportOk || exporting} onClick={() => setExpOpen((o) => !o)}>{exporting ? t("reports.reportsOverviewAdmin.exporting", "Exporting…") : t("common.actions.export", "Export")}</Btn>
            {expOpen ? <>
              <div onClick={() => setExpOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 60 }} />
              <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 300, background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(16,24,40,0.16)", zIndex: 61, overflow: "hidden" }}>
                <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft, fontWeight: 700 }}>{t("reports.overviewAdmin.exportRegistrations", "Export registrations")}</div>
                {/* ui-raw: two-line export menu row (title + description) — OverflowMenuItem renders a single label */}
                <button type="button" onClick={() => { exportCurrent(); setExpOpen(false); }} style={expItem}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("reports.overviewAdmin.currentPeriod", "Current period")}</div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{fmtNum(curRegs)} {t("reports.overviewAdmin.registrationsEveryCustomerDetail", "registrations · every customer detail")}</div>
                </button>
                {cmpMeta.offset != null ? (
                  /* ui-raw: two-line export menu row (title + description) — OverflowMenuItem renders a single label */
                  <button type="button" onClick={() => { exportComparison(); setExpOpen(false); }} style={{ ...expItem, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("reports.overviewAdmin.comparisonPeriod", "Comparison period")}<span style={{ color: T.soft, fontWeight: 400 }}>· {cmpMeta.short}</span></div>
                    <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{fmtNum(cmpRegs)} {t("reports.overviewAdmin.registrations", "registrations")}</div>
                  </button>
                ) : null}
                {cmpMeta.offset != null ? (
                  /* ui-raw: two-line export menu row (title + description) — OverflowMenuItem renders a single label */
                  <button type="button" onClick={() => { exportBoth(); setExpOpen(false); }} style={{ ...expItem, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("reports.overviewAdmin.bothPeriods", "Both periods")}</div>
                    <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{t("reports.overviewAdmin.oneFileTaggedCurrentVsComparison", "One file, tagged current vs comparison")}</div>
                  </button>
                ) : null}
              </div>
            </> : null}
          </div>
        </>}
      />

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Global stats bar — over ALL events, never filtered by the toggle/range.
          * flexShrink: 0 on every direct child of this flex-column scroll region
          * (2026-07-10 PM fix, ingested 2026-07-13): children default to
          * flex-shrink: 1, so overflowing content compressed the sections
          * (clipping stat labels) instead of scrolling. */}
        <div style={{ display: "flex", background: T.n6, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
          {[
            { l: "Locations scanned", v: fmtNum(gStats.stores) },
            { l: "Total events", v: fmtNum(gStats.events) },
            { l: "Total attendees", v: fmtNum(gStats.attendees) },
            { l: "Total spaces", v: fmtNum(gStats.spaces) },
            { l: "Global fill rate", v: `${gStats.fill}%` },
          ].map((s, i) => (
            <div key={s.l} style={{ flex: 1, padding: "14px 18px", borderLeft: i ? `1px solid ${T.border}` : "none", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: T.soft, letterSpacing: 0.05, marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* View toggle (centered pill) — hidden when explicit custom dates define the window */}
        {!isCustom ? (
        <div style={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <SegmentedControl<"past" | "future">
            ariaLabel={t("reports.overviewAdmin.ariaLabel", "Reporting view")}
            value={mode}
            onChange={setMode}
            /* The two reporting views are configuration, not data. */
            options={[
              { value: "past", label: t("reports.overviewAdmin.label12", "Past Performance") },
              { value: "future", label: t("reports.overviewAdmin.label13", "Future Outlook") },
            ]}
          />
        </div>
        ) : (
        <div style={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: T.soft, background: T.n6, borderRadius: 8, padding: "8px 16px" }}>{t("reports.overviewAdmin.customRange", "Custom range ·")}{rangeLabel}</span>
        </div>
        )}

        {/* Findings — the same occurrences read as findings, in the grammar
          * Campus Pulse uses. Above the KPI band on purpose: the numbers are the
          * evidence for these, not the other way round. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
          <FindingsHeader n={findings.length} open={bandOpen} onToggle={() => setBandOpen(!bandOpen)}
            note={`thresholded on fill · under-run < ${UNDER_RUN}% · strong ≥ ${STRONG}%`} />
          {bandOpen ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, alignItems: "stretch" }}>
              {findings.map((f) => (
                <FindingCard key={f.id} f={f} verb={f.verb} filterable={f.occurrences.length > 0}
                  active={finding === f.id}
                  onToggle={() => { const next = finding === f.id ? null : f.id; revealScope.current = next !== null; setFinding(next); }}
                  verbState={verbStates[f.id]} onVerb={() => setConfirmVerb(f)}
                  canAct={canActEvents}
                  actDenyReason="Your role can read this console but not route event work" />
              ))}
            </div>
          ) : null}
          {scope ? (
            <div ref={scopeRef} tabIndex={-1} role="status" aria-live="polite" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "9px 14px", background: alpha(T.p1, 8), border: `1px solid ${T.p1}`, borderRadius: 10, scrollMarginTop: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.p1, whiteSpace: "nowrap" }}>{t("reports.overviewAdmin.text16", "Scoped to “")}{scope.label}” · {scope.n} {t("reports.overviewAdmin.text17", "of")}{filtered.length}</span>
              <span style={{ fontSize: 11.5, color: T.n2 }}>{t("reports.overviewAdmin.text18", "The KPI band, the ranked table and the drill-down below all recompute on these sessions; period-over-period deltas are withheld, because the comparison window holds a different set.")}</span>
              <Btn variant="ghost" onClick={() => setFinding(null)}>{t("reports.overviewAdmin.text19", "Clear")}</Btn>
            </div>
          ) : null}
        </div>

        {/* KPI scorecard — fill-rate hero + revenue/goal blend */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, flexShrink: 0 }}>
          <ExecCard label={t("reports.overviewAdmin.efficiency", "Efficiency")} value={`${exec.efficiency}%`} accent={T.blue1} sub={t("reports.overviewAdmin.sub", "Attendees ÷ spaces")} delta={dEff} />
          <ExecCard label={t("reports.overviewAdmin.perfectExecution", "Perfect execution")} value={fmtNum(exec.perfect)} accent={T.green1} sub={t("reports.overviewAdmin.sub2", "Events filled ≥ 80%")} delta={dPerfect} />
          <ExecCard label={t("reports.overviewAdmin.missedOpps", "Missed opps")} value={fmtNum(exec.missed)} accent={T.red1} sub={t("reports.overviewAdmin.sub3", "Events filled < 30%")} delta={dMissed} />
          <ExecCard label={t("reports.overviewAdmin.attendees", "Attendees")} value={fmtNum(exec.attendees)} accent={T.orange1} sub={t("reports.overviewAdmin.sub5", "{length} occurrences", { length: filtered.length })} delta={dAtt} />
          <RevenueCard value={fmtMoney(b.kpis.gross)} accent={T.p1} deltaText={revDeltaText} up={!!dRev?.up} goalPct={revGoalPct} />
        </div>

        {/* Every session, ranked — the By-event report merged in (2026-08-31).
          * It ranked the same occurrences this console already holds against the
          * same benchmark, so it was a second tab answering one question. The two
          * leaderboards it sat beside are gone: "attention needed" is a finding
          * with an owner now, and "top performing" is the top of this table. */}
        <Card style={{ overflow: "hidden", flexShrink: 0 }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.overviewAdmin.text20", "Every session, ranked")}</h3>
              <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{t("reports.overviewAdmin.text21", "Fill against the window’s own average, highest first. Sessions with no capacity set are excluded — see the data-quality finding.")}</div>
            </div>
            <span style={{ fontSize: 11, color: T.soft, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {ranked.length} {t("reports.overviewAdmin.text17", "of")}{shown.length} {t("reports.overviewAdmin.text22", "occurrences · window average")}{execWindowAvg}%
            </span>
          </div>
          {ranked.length === 0 ? (
            <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 13, color: T.soft }}>{t("reports.overviewAdmin.text23", "No session in this view has a capacity set, so nothing can be ranked.")}</div>
          ) : (
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                    <th style={{ ...thStyle, width: 44, textAlign: "right" }}>#</th>
                    <th style={thStyle}>{t("reports.overviewAdmin.text24", "Session")}</th>
                    <th style={thStyle}>{t("reports.overviewAdmin.text25", "Location")}</th>
                    <th style={thStyle}>{t("reports.overviewAdmin.text26", "Date")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.overviewAdmin.text27", "Booked")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.overviewAdmin.text28", "Fill")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.overviewAdmin.text29", "vs average")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((o, i) => {
                    const d = o.fill - execWindowAvg;
                    return (
                      <tr key={o.id}>
                        <td style={{ ...tdStyle, textAlign: "right", color: T.soft, fontVariantNumeric: "tabular-nums" }}>{i + 1}</td>
                        <td style={{ ...tdStyle, color: T.n1, fontWeight: 600 }}>{o.name}</td>
                        <td style={tdStyle}>{o.storeName}</td>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{o.displayDate} · {o.time}</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", color: T.n1 }}>{o.booked} / {o.spaces}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}><FillPill pct={o.fill} size={12} /></td>
                        <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", color: Math.abs(d) < 1 ? T.soft : d > 0 ? T.green1 : T.red1, fontWeight: 600 }}>
                          {Math.abs(d) < 1 ? "—" : t("reports.overviewAdmin.pointsDelta", "{sign}{points} pts", { sign: d > 0 ? "+" : "", points: Math.round(d) })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Grouped drill-down */}
        <Card style={{ overflow: "hidden", flexShrink: 0 }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.overviewAdmin.eventGroupName", "Event group name")}</h3>
              <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{t("reports.overviewAdmin.fillRateRegistrationsCapacityRostersAndExportsContainEachEventSReal", "Fill rate = registrations ÷ capacity. Rosters and exports contain each event's real registrations.")}</div>
            </div>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>{i18nT("reports.overviewAdmin.analysisAmpStatistics.rendered", "Analysis & statistics")}</span>
          </div>

          {months.length === 0 ? (
            <div style={{ padding: "48px 24px", textAlign: "center", color: T.soft, fontSize: 14 }}>
              {t("reports.overviewAdmin.noEventsFoundForTheSelected", "No events found for the selected")}{" "}{modeLabel} {t("reports.overviewAdmin.dateRangeTrySwitchingTheViewModeOrAdjustingTheRange", "date range. Try switching the view mode or adjusting the range.")}</div>
          ) : months.map((m: MonthGroup) => {
            const mOpen = openMonths.has(m.monthSort);
            return (
              <div key={m.monthSort} style={{ borderBottom: `1px solid ${T.border}` }}>
                {/* ui-raw: disclosure header row for a month group — Disclosure's pattern, not Button's */}
                <button type="button" onClick={() => toggleSet(setOpenMonths, m.monthSort)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: "none", background: T.n6, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                  <Icon.chev size={12} dir={mOpen ? "down" : "right"} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{m.monthYear}</span>
                  <span style={{ fontSize: 12, color: T.soft }}>· {t("reports.overviewAdmin.eventCount.icu", "{count, plural, one {# event} other {# events}}", { count: m.events.length })}</span>
                </button>

                {mOpen && m.events.map((ev) => {
                  const key = `${m.monthSort}::${ev.name}`;
                  const eOpen = openEvents.has(key);
                  return (
                    <div key={key} style={{ borderTop: `1px solid ${T.border}`, borderLeft: `3px solid ${T.p1}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px" }}>
                        {/* ui-raw: disclosure row for one event — Disclosure's pattern; nested inside the month disclosure, which Disclosure cannot express (single-item only, no Accordion) */}
                        <button type="button" onClick={() => toggleSet(setOpenEvents, key)}
                          style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left", padding: 0 }}>
                          <Icon.chev size={11} dir={eOpen ? "down" : "right"} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.name}</span>
                          <span style={{ fontSize: 12, color: T.soft }}>({ev.occurrences.length})</span>
                        </button>
                        <span style={{ fontSize: 12, color: T.soft, fontVariantNumeric: "tabular-nums" }}>{fmtNum(ev.booked)} / {fmtNum(ev.spaces)}</span>
                        <FillPill pct={ev.fill} />
                        <Btn variant="ghost" size="sm" icon={<Icon.csv size={12} />} disabled={!exportOk || exporting} onClick={() => downloadGroupRosters(ev.name, ev.occurrences)}>{t("reports.overviewAdmin.downloadAllRosters", "Download all rosters")}</Btn>
                      </div>

                      {eOpen && (
                        <div style={{ padding: "0 16px 12px 30px" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                            <thead>
                              <tr style={{ color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                                <th style={thStyle}>{t("reports.overviewAdmin.date", "Date")}</th>
                                <th style={thStyle}>{t("reports.overviewAdmin.location", "Location")}</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.overviewAdmin.booked", "Booked")}</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.overviewAdmin.fill", "Fill %")}</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.overviewAdmin.roster", "Roster")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ev.occurrences.map((o) => (
                                <tr key={o.id} style={{ borderTop: `1px solid ${T.border}` }}>
                                  <td style={tdStyle}>
                                    <div style={{ color: T.n1, fontWeight: 500 }}>{o.displayDate}</div>
                                    <div style={{ color: T.soft, fontSize: 11 }}>{o.time}</div>
                                  </td>
                                  <td style={tdStyle}>
                                    <span style={{ color: T.n1 }}>{o.storeName}</span>
                                  </td>
                                  <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", color: T.n1 }}>{o.booked} / {o.spaces}</td>
                                  <td style={{ ...tdStyle, textAlign: "right" }}><FillPill pct={o.fill} size={12} /></td>
                                  <td style={{ ...tdStyle, textAlign: "right" }}>
                                    <Btn variant="ghost" size="sm" icon={<Icon.csv size={12} />} disabled={!exportOk || exporting} onClick={() => downloadRoster(o)}>{t("reports.overviewAdmin.csv", "CSV")}</Btn>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Card>
      </div>
      <ConfirmModal open={!!confirmVerb} title={confirmVerb && confirmVerb.verb ? confirmVerb.verb.label : t("reports.overviewAdmin.title", "Route this")}
        confirmLabel={t("reports.overviewAdmin.confirmLabel", "Record the hand-off")}
        onClose={() => setConfirmVerb(null)}
        onConfirm={() => { const f = confirmVerb; setConfirmVerb(null); if (f) runVerb(f); }}
        message={confirmVerb ? (
          <span style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: T.n2 }}>
            <span style={{ color: T.n1, fontWeight: 600 }}>{confirmVerb.action}</span>
            <span>{t("reports.overviewAdmin.text31", "Owner —")}{confirmVerb.owner}</span>
            <span>{confirmVerb.occurrences.length} {confirmVerb.occurrences.length === 1 ? t("reports.overviewAdmin.text32", "session") : t("reports.overviewAdmin.text33", "sessions")}{t("reports.overviewAdmin.text34", ". Nothing is sent to an attendee: the hand-off is recorded against this finding in the audit trail, with you as the actor.")}</span>
            <span style={{ fontSize: 10.5, color: T.soft, fontFamily: T.fontMono }}>{confirmVerb.method}</span>
          </span>
        ) : null} />
    </Screen>
  );
}


/* ── Local styles ─────────────────────────────────────────────────────── */
const selStyle: React.CSSProperties = { height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, fontFamily: T.font, fontSize: 13, color: T.n1, cursor: "pointer", appearance: "auto", background: T.white };
const dateStyle: React.CSSProperties = { height: 36, padding: "0 10px", border: `1px solid ${T.n4}`, borderRadius: 6, fontFamily: T.font, fontSize: 13, color: T.n1, background: T.white };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "8px 10px" };
const tdStyle: React.CSSProperties = { padding: "9px 10px", verticalAlign: "top" };
const linkBtn: React.CSSProperties = { border: "none", background: "transparent", color: T.p1, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font, padding: 0 };
const expItem: React.CSSProperties = { display: "block", width: "100%", textAlign: "left", border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, padding: "10px 14px" };

/* ── Location filter — searchable multi-select ─────────────────────────── */
function LocationMultiSelect({ options, selected, onChange }: { options: { id: string; name: string }[]; selected: Set<string>; onChange: (s: Set<string>) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = options.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));
  const summary = selected.size === 0 ? "All locations" : selected.size === 1 ? [...selected][0] : `${selected.size} locations`;
  const toggle = (name: string) => { const n = new Set(selected); n.has(name) ? n.delete(name) : n.add(name); onChange(n); };
  return (
    <div style={{ position: "relative" }}>
      {/* ui-raw: MultiSelect trigger (summary text + caret, focus ring on open) — MultiSelect's pattern, not Button's */}
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ ...selStyle, display: "inline-flex", alignItems: "center", gap: 8, minWidth: 160, maxWidth: 230 }} aria-label={t("reports.overviewAdmin.locations", "Locations")} title={t("reports.overviewAdmin.filterByLocation", "Filter by location")}>
        <Icon.loc size={13} style={{ color: T.soft }} />
        <span style={{ flex: 1, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{summary}</span>
        <Icon.chev size={11} dir={open ? "up" : "down"} />
      </button>
      {open ? <>
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 60 }} />
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 280, background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(16,24,40,0.16)", zIndex: 61, overflow: "hidden" }}>
          <div style={{ padding: 8, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, height: 32, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 6 }}>
              <Icon.search size={13} style={{ color: T.soft }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("reports.overviewAdmin.searchLocations", "Search locations…")} autoFocus style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, fontFamily: T.font, color: T.n1 }} />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 12px", borderBottom: `1px solid ${T.border}` }}>
            <Btn variant="link" size="sm" onClick={() => onChange(new Set(options.map((o) => o.name)))}>{t("reports.overviewAdmin.selectAll", "Select all")}</Btn>
            <Btn variant="link" size="sm" onClick={() => onChange(new Set())}>{t("common.actions.clear", "Clear")}</Btn>
          </div>
          <div style={{ maxHeight: 240, overflow: "auto" }}>
            {filtered.length === 0 ? <div style={{ padding: 12, fontSize: 13, color: T.soft }}>{t("reports.overviewAdmin.noMatches", "No matches")}</div> : filtered.map((o) => {
              const on = selected.has(o.name);
              return (
                /* ui-raw: MultiSelect option row (checkbox + label, p4 active) — MultiSelect/Combobox's pattern, not Button's */
                <button key={o.id} type="button" onClick={() => toggle(o.name)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "none", background: on ? T.p4 : "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                  <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on ? T.p1 : T.n4}`, background: on ? T.p1 : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on ? <Icon.check size={11} style={{ color: T.white }} /> : null}</span>
                  <span style={{ fontSize: 13, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </> : null}
    </div>
  );
}
